import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import {
  isMarketingCampaignClientContest,
  isMarketingCampaignClientRebate,
  isMarketingCampaignLuckyDraw,
  isMarketingCampaignMarginBonus,
} from 'api/Trading-Accounts/Types/MarketingCampaignType'
import { Loading } from 'components/Loading'
import { Spinner } from 'components/Spinner'
import PageNotFound from 'routes/PageNotFound/PageNotFound'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links, marketingCampaignDetailsRouteUrl } from 'utils/featureToggles'

import { CampaignClientContestDetailsResultsPage } from './CampaignClientContestDetailsResultsPage'
import CampaignDetailsCountryExceptionsPage from './CampaignDetailsCountryExceptionsPage'
import CampaignDetailsPage from './CampaignDetailsPage/CampaignDetailsPage'
import { CampaignIbContestDetailsResultsPage } from './CampaignIbContestDetailsResultsPage'
import CampaignLuckyDrawDetailsResultsPage from './CampaignLuckyDrawDetailsResultsPage'
import CampaignLuckyDrawDetailsRewardsPage from './CampaignLuckyDrawDetailsRewardsPage'
import { CampaignMarginBonusDetailsResultsPage } from './CampaignMarginBonusDetailsResultsPage'
import { CampaignRebateDetailsResultsPage } from './CampaignRebateDetailsResultsPage'

export enum CampaignTab {
  DETAILS = 'details',
  PARTICIPATING_COUNTRIES = 'participating-countries',
  RESULTS = 'results',
  REWARDS = 'lucky-draw-rewards',
}

const ResolveCampaignRoute = (): JSX.Element => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [crumbs, setCrumbs] = useState([
    { title: 'Marketing Campaigns', link: links.marketingCampaigns.url },
  ])

  const { id, slug } = useParams<{ id: string; slug: CampaignTab }>()
  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? tradingAccountsApiClient.getMarketingCampaign(id) : Promise.resolve(null)),
    [id]
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (leadDetailsSlug?: CampaignTab) => {
    if (!leadDetailsSlug) {
      return false
    }
    return (
      leadDetailsSlug === CampaignTab.DETAILS ||
      leadDetailsSlug === CampaignTab.PARTICIPATING_COUNTRIES ||
      leadDetailsSlug === CampaignTab.RESULTS ||
      leadDetailsSlug === CampaignTab.REWARDS
    )
  }

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data.name,
          link: marketingCampaignDetailsRouteUrl(
            data,
            slug ? (matchesSlug(slug) ? slug : CampaignTab.DETAILS) : CampaignTab.DETAILS
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!data) {
      return <PageNotFound />
    }
    if (slug === CampaignTab.DETAILS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <CampaignDetailsPage data={data} afterSubmit={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === CampaignTab.PARTICIPATING_COUNTRIES) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <CampaignDetailsCountryExceptionsPage data={data} forceRefresh={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === CampaignTab.REWARDS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <CampaignLuckyDrawDetailsRewardsPage data={data} />
        </React.Fragment>
      )
    }
    if (slug === CampaignTab.RESULTS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <CampaignDetailsResultsPage data={data} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

interface OuterProps {
  data: MarketingCampaignDetailDto
}

const CampaignDetailsResultsPage: React.FC<OuterProps> = ({ data }) => {
  if (isMarketingCampaignLuckyDraw(data.type.id)) {
    return <CampaignLuckyDrawDetailsResultsPage data={data} />
  }
  if (isMarketingCampaignClientContest(data.type.id)) {
    return <CampaignClientContestDetailsResultsPage data={data} />
  }
  if (isMarketingCampaignClientRebate(data.type.id)) {
    return <CampaignRebateDetailsResultsPage data={data} />
  }
  if (isMarketingCampaignMarginBonus(data.type.id)) {
    return <CampaignMarginBonusDetailsResultsPage data={data} />
  }
  return <CampaignIbContestDetailsResultsPage data={data} />
}

export default ResolveCampaignRoute
